//? PLUGINS //
import { Box, Card, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect } from "react";

//? COMPONENTS //

import Content from "pages/requests/stepsIndividual/step1/step1.content";

import * as Styles from "pages/requests/stepsIndividual/step1/step1.styles";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { isDate } from "lodash";
import countries from "resources/countries_en_nl.json";
import i18next from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as applicationService from "services/application-service";
import { validate } from "uuid";
import { idValidation } from "utilities/idValidation";
import { isNationalityBelgian } from "pages/requests/formHelper";

export interface INITIAL_FORM_STATE_TYPE {
    userFirstName?: string,
    userLastName?: string,
    userBirthDay?: string,
    userNationality?: string | null,
    userBirthPlace?: string,
    userRRN?: string,
    userAddressStreet?: string,
    userAddressHouseNumber?: string,
    userAddressBox?: string,
    userAddressZip?: string,
    userAddressCity?: string,
    userAddressCountry?: string | null,
    userEmail?: string,
};

export const INITIAL_FORM_STATE: INITIAL_FORM_STATE_TYPE = {
    userFirstName: "",
    userLastName: "",
    userBirthDay: "",
    userNationality: null,
    userBirthPlace: "",
    userRRN: "",
    userAddressStreet: "",
    userAddressHouseNumber: "",
    userAddressBox: "",
    userAddressZip: "",
    userAddressCity: "",
    userAddressCountry: null,
    userEmail: "",
};

export const FORM_VALIDATION = (t: any) => {
    const locale = i18next.language === undefined ? "nl" : i18next.language;
    dayjs.extend(customParseFormat);
    return Yup.object().shape({
        userFirstName: Yup.string()
            .matches(/^(?!\d+$).+/, 'yup.invalid')
            .required("yup.required"),
        userLastName: Yup.string()
            .matches(/^(?!\d+$).+/, 'yup.invalid')
            .required("yup.required"),
        userBirthDay: Yup.date()
            .typeError("yup.validDate")
            .test("is-today", "yup.step1.birthDay", (date) => {
                // console.log(dayjs().diff(date, "years"));
                return dayjs().diff(date, "years") >= 14;
            })
            .required("yup.required"),
        userNationality: Yup.string()
            .nullable()
            .test('value', 'yup.validNationality', (val) => {
                const nationalties = countries.filter((_, index) => {
                    const option: any = countries[index]
                    if (option.nationality !== undefined) return option
                }).map((_, index) => {
                    const option = countries[index];
                    if (locale === 'en') return option?.nationality?.en;
                    if (locale === 'fr') return option?.nationality?.fr;
                    return option?.nationality?.nl
                })
                if (val !== null && val !== undefined) return nationalties.includes(val);
                return false;
            })
            .required("yup.required"),
        userBirthPlace: Yup.string()
            .required("yup.required")
            .nullable(),
        userRRN: Yup.object().when('userNationality', (userNationality) => {
            if (isNationalityBelgian(userNationality)) {
                return Yup.string()
                    // .matches(/[0-9]{11}/gi, t("yup.step1.id"))
                    .test('value', 'yup.step1.rrnUnValid', (val) => {
                        return idValidation(val);
                    })
                    .typeError("yup.step1.numberType")
                    .required("yup.required")
                    .nullable();
            }
            return Yup.string().required('yup.required').nullable();
        }),
        userAddressStreet: Yup.string()
            .matches(/^(?!\d+$).+/, 'yup.invalid')
            .required("yup.required"),
        userAddressHouseNumber: Yup.string()
            .matches(/.*\d.*/, 'yup.invalid')
            .required("yup.required"),
        userAddressBox: Yup.string()
            .optional(),
        userAddressZip: Yup.string()
            .when('userNationality', (userNationality) => {
                if (isNationalityBelgian(userNationality)) {
                    return Yup.string().matches(/^\d{4}$/, 'yup.invalid').length(4);
                }
                return Yup.string();
            })
            .required("yup.required"),
        userAddressCity: Yup.string()
            .required("yup.required"),
        userAddressCountry: Yup.string()
            .nullable()
            .test('value', 'yup.validCountry', (val) => {
                const nationalties = countries.filter((_, index) => {
                    const option: any = countries[index]
                    if (option.name !== undefined) return option
                }).map((_, index) => {
                    const option: any = countries[index];
                    if (locale === 'en') return option?.name?.en;
                    if (locale === 'fr') return option?.name?.fr;
                    return option?.name?.nl
                })
                if (val !== null && val !== undefined) return nationalties.includes(val);
                return false;
            })
            .required("yup.required"),
        userEmail: Yup.string()
            .email("yup.email")
            .required("yup.required"),
    });
};

interface Props {
    title: string;
    onBackClick: () => void;
    titlePath?: string;
}

const Step1 = (props: Props) => {
    const { title, onBackClick, titlePath } = props;
    const theme = useTheme();
    const { applicationId, completionHash } = useParams();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (applicationId !== undefined && completionHash === undefined) {
            applicationService.checkGuid(applicationId).then((res: boolean) => {
                if (!res) {
                    navigate('/')
                }
            });
        }
    }, []);

    // {
    // extra-small
    //   xs: 0,
    // small
    //   sm: 600,
    // medium
    //   md: 900,
    // large
    //   lg: 1200,
    // extra-large
    //   xl: 1536,
    //   }

    const isMobile = useMediaQuery(theme.breakpoints.between("xs", "lg"));

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                mt: "50px",
            }}
        >
            {isMobile ? (
                <Content title={title} onBackClick={onBackClick} titlePath={titlePath} />
            ) : (
                <Card sx={Styles.cardCSS(theme)}>
                    <Content title={title} onBackClick={onBackClick} titlePath={titlePath} />
                </Card>
            )}
        </Box>
    );
};

export default Step1;
